/**
 * Risk & Threats — Insights Page
 *
 * Answers: "What are our risks and the threats likely to impact our organization?"
 * Tabbed layout: Threat Landscape / Financial Exposure / Investment Priorities
 */

import { useState, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useInsightsData } from '../hooks/useInsightsData'
import InsightCard from '../components/InsightCard'
import ThreatProfileSummary from '../components/ThreatProfileSummary'
import RelevantThreatActorsCard from '../components/RelevantThreatActorsCard'
import ExceedanceCurveChart from '../components/ExceedanceCurveChart'
import ThreatContextStep from '../../assessment/components/ThreatContextStep'
import InvestmentComparisonChart from '../components/InvestmentComparisonChart'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { compareBeforeAfter } from '../../../shared/utils/monteCarloSimulation'
import { calculateDynamicCalibration } from '../../../shared/utils/dynamicScenarioCalibration'
import { supabase } from '../../../shared/lib/supabase'
import { useTechnologyStack } from '../../assessment/hooks/useTechnologyStack'

function fmt(v) {
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`
  if (v >= 1000) return `$${Math.round(v / 1000)}K`
  return `$${Math.round(v)}`
}

// ── Risk Profile Tab ─────────────────────────────────────────────────────────

function getRiskRating(maturityScore, incidentProb, coverageRatio) {
  // Composite: low maturity + high incident probability + poor coverage = critical
  let score = 0
  if (maturityScore < 20) score += 3
  else if (maturityScore < 40) score += 2
  else if (maturityScore < 60) score += 1

  if (incidentProb > 70) score += 3
  else if (incidentProb > 50) score += 2
  else if (incidentProb > 30) score += 1

  if (coverageRatio < 0.3) score += 2
  else if (coverageRatio < 0.7) score += 1

  if (score >= 7) return { level: 'Critical', color: '#EF4444', bg: 'bg-red-50 dark:bg-red-900/10', border: 'border-red-200 dark:border-red-800/30', text: 'text-red-700 dark:text-red-400' }
  if (score >= 5) return { level: 'High', color: '#F97316', bg: 'bg-orange-50 dark:bg-orange-900/10', border: 'border-orange-200 dark:border-orange-800/30', text: 'text-orange-700 dark:text-orange-400' }
  if (score >= 3) return { level: 'Medium', color: '#F59E0B', bg: 'bg-amber-50 dark:bg-amber-900/10', border: 'border-amber-200 dark:border-amber-800/30', text: 'text-amber-700 dark:text-amber-400' }
  return { level: 'Low', color: '#22C55E', bg: 'bg-green-50 dark:bg-green-900/10', border: 'border-green-200 dark:border-green-800/30', text: 'text-green-700 dark:text-green-400' }
}

function RiskProfileTab({ raw, data, tp, incidentProb, p90, client }) {
  const scores = raw?.scores
  const maturityScore = scores?.overall?.score || 0
  const maturityLevel = scores?.overall?.maturityLevel?.label || 'Unknown'
  const p50 = raw?.monteCarloResults?.aggregateDistribution?.percentiles?.p50 || 0
  const insurance = raw?.insuranceAdequacy || {}
  const coverageLimit = insurance.coverageLimit || 0
  const recommendedCoverage = insurance.recommendedCoverage || 1
  const coverageRatio = recommendedCoverage > 0 ? coverageLimit / recommendedCoverage : 0
  const lossScenarios = raw?.lossScenarios?.scenarios || []

  const riskRating = getRiskRating(maturityScore, incidentProb || 0, coverageRatio)

  // Build risk drivers — connecting gaps to threats
  const riskDrivers = useMemo(() => {
    const drivers = []
    const categories = scores?.maturity?.categoryScores || {}
    const topActor = tp.topActors?.[0]
    const topVector = tp.topVectors?.[0]
    const topAsset = tp.topAssets?.[0]
    const highestScenario = lossScenarios[0]

    // Find weakest categories — values may be objects {name, score, ...} or numbers
    const parsed = Object.entries(categories).map(([key, val]) => ({
      key,
      name: (typeof val === 'object' ? val.name : key)?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || key,
      score: typeof val === 'object' ? (val.score ?? 0) : (val ?? 0)
    }))
    const weakest = parsed.sort((a, b) => a.score - b.score).slice(0, 3)

    // Connect weakest domain to highest threat
    if (weakest[0] && topActor) {
      drivers.push({
        severity: weakest[0].score < 20 ? 'critical' : weakest[0].score < 40 ? 'high' : 'medium',
        statement: `${weakest[0].name} maturity at ${weakest[0].score}% leaves the organization exposed to ${topActor.name} (${Math.round((topActor.adjustedLikelihood || topActor.likelihood || 0) * 100)}% likelihood)`
      })
    }

    // Connect attack vector to control gap
    if (topVector && weakest[1]) {
      drivers.push({
        severity: weakest[1].score < 20 ? 'critical' : weakest[1].score < 40 ? 'high' : 'medium',
        statement: `${topVector.name} attacks (${Math.round((topVector.adjustedPrevalence || topVector.prevalence || 0) * 100)}% prevalence) exploit gaps in ${weakest[1].name} (${weakest[1].score}%)`
      })
    }

    // Financial exposure vs coverage
    if (highestScenario && coverageLimit > 0) {
      const gap = highestScenario.grossLoss - coverageLimit
      if (gap > 0) {
        drivers.push({
          severity: gap > coverageLimit ? 'critical' : 'high',
          statement: `${highestScenario.scenarioName} expected loss of ${fmt(highestScenario.grossLoss)} exceeds ${fmt(coverageLimit)} coverage by ${fmt(gap)}`
        })
      }
    } else if (highestScenario && coverageLimit === 0) {
      drivers.push({
        severity: 'critical',
        statement: `No cyber insurance in place against ${fmt(highestScenario.grossLoss)} expected loss from ${highestScenario.scenarioName}`
      })
    }

    // Target asset exposure
    if (topAsset && weakest[2]) {
      drivers.push({
        severity: 'medium',
        statement: `${topAsset.name} (${Math.round((topAsset.adjustedCriticality || topAsset.criticality || 0) * 100)}% criticality) at risk due to gaps in ${weakest[2].name} (${weakest[2].score}%)`
      })
    }

    // Incident probability
    if (incidentProb && incidentProb > 50) {
      drivers.push({
        severity: incidentProb > 75 ? 'critical' : 'high',
        statement: `${incidentProb}% annual probability of experiencing at least one cyber incident based on current controls`
      })
    }

    return drivers.slice(0, 5)
  }, [scores, tp, lossScenarios, coverageLimit, incidentProb])

  return (
    <div className="space-y-5">
      {/* Risk Rating Hero */}
      <div className={`rounded-xl border p-6 ${riskRating.bg} ${riskRating.border}`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Overall Risk Rating</p>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold" style={{ color: riskRating.color }}>{riskRating.level}</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Based on {maturityLevel} maturity ({maturityScore}%), {incidentProb || 'N/A'}% incident likelihood, and {p50 > 0 ? `${fmt(p50)} expected annual exposure` : 'incomplete impact data'}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{maturityScore}%</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">Maturity Score</p>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{incidentProb || 'N/A'}%</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">Incident Likelihood</p>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{p50 ? fmt(p50) : 'N/A'}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">Expected Annual Exposure</p>
            </div>
          </div>
        </div>
        <div className="mt-3 space-y-1">
          {p90 > 0 && (
            <p className="text-[10px] text-gray-400 dark:text-gray-500">
              Expected Annual Exposure is the median simulated gross annual loss before insurance. In a severe year (10% chance), gross losses could reach {fmt(p90)}.
              {coverageLimit > 0 && ` Current coverage of ${fmt(coverageLimit)} would offset a portion of these losses, subject to policy terms.`}
            </p>
          )}
          <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">
            Insurance estimates are for planning purposes only. Actual coverage is subject to policy limitations, exclusions, sub-limits, and waiting periods. Review all coverage details with your insurance carrier or a qualified insurance professional.
          </p>
        </div>
      </div>

      {/* Risk Drivers */}
      {riskDrivers.length > 0 && (
        <InsightCard title="Risk Drivers" subtitle="How threats, gaps, and financial exposure combine to create organizational risk">
          <div className="space-y-3">
            {riskDrivers.map((driver, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <span className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${
                  driver.severity === 'critical' ? 'bg-red-500' :
                  driver.severity === 'high' ? 'bg-orange-500' :
                  'bg-amber-500'
                }`} />
                <p className="text-sm text-gray-700 dark:text-gray-300">{driver.statement}</p>
              </div>
            ))}
          </div>
        </InsightCard>
      )}

      {/* Risk Matrix — Maturity vs Threat for key domains */}
      {scores?.maturity?.categoryScores && tp.topActors?.length > 0 && (
        <InsightCard title="Domain Risk Exposure" subtitle="Security domains ranked by the gap between current maturity and threat relevance">
          <div className="space-y-2">
            {(() => {
              const categories = scores.maturity.categoryScores || {}
              // Pair each category with a risk score: lower maturity + higher threat = higher risk
              const domainRisks = Object.entries(categories).map(([domain, val]) => {
                const score = typeof val === 'object' ? (val.score ?? 0) : (val ?? 0)
                const name = typeof val === 'object' ? (val.name || domain) : domain
                const threatFactor = Math.max(
                  (tp.topActors?.[0]?.adjustedLikelihood || 0),
                  (tp.topVectors?.[0]?.adjustedPrevalence || 0)
                )
                const riskExposure = Math.round((1 - score / 100) * threatFactor * 100)
                return { domain, name, maturity: score, riskExposure }
              })
              .sort((a, b) => b.riskExposure - a.riskExposure)
              .slice(0, 8)

              return domainRisks.map(d => {
                return (
                  <div key={d.domain} className="flex items-center gap-3 py-1.5">
                    <div className="w-40 min-w-0">
                      <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{d.name}</p>
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${d.riskExposure}%`,
                            backgroundColor: d.riskExposure >= 60 ? '#EF4444' : d.riskExposure >= 40 ? '#F59E0B' : '#22C55E'
                          }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right">{d.riskExposure}%</span>
                    </div>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 w-20 text-right">Maturity: {d.maturity}%</span>
                  </div>
                )
              })
            })()}
          </div>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-3">
            Risk exposure = (100 - maturity %) x top threat likelihood. Higher values indicate domains where low maturity intersects with high threat activity.
          </p>
        </InsightCard>
      )}

      {/* Risk-Maturity Alignment */}
      {raw?.alignments?.length > 0 && (
        <InsightCard title="Risk Appetite Alignment" subtitle="Does your security posture match your stated risk tolerance?">
          {(() => {
            const alignments = raw.alignments
            const aligned = alignments.filter(a => a.alignment?.label === 'Aligned' || Math.abs(a.gap || 0) <= 10)
            const misaligned = alignments.filter(a => a.alignment?.label !== 'Aligned' && Math.abs(a.gap || 0) > 10)

            return (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  <span className="font-medium text-gray-900 dark:text-white">{aligned.length} of {alignments.length}</span> risk domains are aligned with stated tolerance.
                  {misaligned.length > 0 && ` ${misaligned.length} domain${misaligned.length > 1 ? 's require' : ' requires'} attention.`}
                </p>

                <div className="space-y-2">
                  {alignments.map(a => {
                    const appetiteScore = a.riskAppetite?.score || 0
                    const appetiteLabel = appetiteScore >= 4 ? 'High' : appetiteScore >= 3 ? 'Moderate' : 'Low'
                    const maturity = a.relatedMaturity?.average || 0
                    const isAligned = Math.abs(a.gap || 0) <= 10
                    const isConcern = a.isConcern

                    return (
                      <div key={a.domain} className={`flex items-center gap-4 p-3 rounded-lg ${isConcern ? 'bg-red-50 dark:bg-red-900/10' : isAligned ? 'bg-green-50 dark:bg-green-900/10' : 'bg-amber-50 dark:bg-amber-900/10'}`}>
                        <div className="w-44 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{a.domain}</p>
                        </div>
                        <div className="flex items-center gap-6 flex-1">
                          <div className="text-center w-20">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Appetite</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{appetiteLabel}</p>
                          </div>
                          <div className="text-center w-20">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Maturity</p>
                            <p className={`text-sm font-bold ${maturity < 30 ? 'text-red-600 dark:text-red-400' : maturity < 60 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>{maturity}%</p>
                          </div>
                          <div className="text-center w-20">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Gap</p>
                            <p className={`text-sm font-bold ${a.gap >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{a.gap > 0 ? '+' : ''}{a.gap}%</p>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {isAligned ? (
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Aligned</span>
                          ) : isConcern ? (
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">At Risk</span>
                          ) : (
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">Misaligned</span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}
        </InsightCard>
      )}
    </div>
  )
}

// Initiative metadata
const INITIATIVE_DETAILS = {
  mfa: { label: 'Multi-Factor Authentication', description: 'Deploy MFA across all user accounts and administrative access', enhancedDescription: 'Validate MFA coverage, enforce conditional access policies, and close bypass gaps', costMin: 5000, costMax: 15000, impact: 'Reduces credential-based attacks', effort: 'Low', impactLevel: 'High' },
  endpoint: { label: 'Endpoint Detection & Response', description: 'Deploy EDR on all endpoints with managed detection', enhancedDescription: 'Optimize EDR policies, validate coverage across all endpoints, and tune detection rules', costMin: 10000, costMax: 30000, impact: 'Reduces ransomware and malware risk', effort: 'Medium', impactLevel: 'High' },
  backup: { label: 'Backup Validation & Recovery', description: 'Implement immutable backups with tested recovery procedures', enhancedDescription: 'Test recovery procedures, validate immutability, and reduce RTO through automation', costMin: 5000, costMax: 20000, impact: 'Reduces recovery time and extortion costs', effort: 'Low', impactLevel: 'High' },
  patching: { label: 'Patch Management Program', description: 'Risk-based patching with defined SLA timelines', enhancedDescription: 'Formalize patch SLAs, automate critical patching, and close visibility gaps', costMin: 5000, costMax: 10000, impact: 'Reduces vulnerability exposure window', effort: 'Low', impactLevel: 'Medium' },
  segmentation: { label: 'Network Segmentation', description: 'Segment critical systems from general user networks', enhancedDescription: 'Validate segmentation controls, test isolation effectiveness, and close firewall rule gaps', costMin: 15000, costMax: 40000, impact: 'Limits lateral movement and blast radius', effort: 'High', impactLevel: 'High' },
  monitoring: { label: 'SIEM / SOC Monitoring', description: '24/7 security monitoring with centralized log management', enhancedDescription: 'Tune detection rules, expand log source coverage, and reduce mean time to detect', costMin: 20000, costMax: 60000, impact: 'Reduces detection and response time', effort: 'High', impactLevel: 'High' },
  firewall: { label: 'Next-Generation Firewall', description: 'NGFW with IPS, application control, and threat intelligence', enhancedDescription: 'Optimize firewall rules, enable advanced threat prevention features, and validate policy effectiveness', costMin: 10000, costMax: 25000, impact: 'Reduces external intrusion risk', effort: 'Medium', impactLevel: 'Medium' },
  identity: { label: 'Identity Platform', description: 'Centralized identity with conditional access and SSO', enhancedDescription: 'Enforce conditional access policies, expand SSO coverage, and implement privileged access management', costMin: 10000, costMax: 20000, impact: 'Reduces unauthorized access and account takeover', effort: 'Medium', impactLevel: 'High' },
}

// Generate dynamic implementation notes based on current control state
function getImplementationNote(initId, maturityResponses, techResponses) {
  const ma = maturityResponses || {}
  const ts = techResponses || {}

  const notes = {
    mfa: {
      no: 'No MFA in place. Deploy MFA for all user accounts, starting with admin and privileged access.',
      partially: 'MFA partially deployed. Extend coverage to all users and enforce for remote access and cloud applications.',
      mostly: 'MFA broadly deployed. Close remaining gaps: service accounts, legacy applications, and conditional access policies.',
      yes: 'MFA fully deployed. Validate coverage periodically, review bypass rules, and ensure conditional access policies are current.'
    },
    endpoint: {
      none: 'No endpoint protection. Deploy EDR solution across all endpoints immediately.',
      'Traditional AV only': 'Legacy AV only. Upgrade to EDR with behavioral detection, managed threat hunting, and automated response.',
      default: 'EDR in place. Validate agent deployment coverage, tune alert thresholds, and ensure response playbooks are tested.'
    },
    backup: {
      no: 'No tested backup procedures. Implement immutable backups with automated testing and documented recovery runbooks.',
      partially: 'Backups exist but are not regularly tested. Establish quarterly recovery testing and validate immutability settings.',
      mostly: 'Backup testing in place but not comprehensive. Extend testing to all critical systems and validate RTO/RPO targets.',
      yes: 'Backup and recovery fully tested. Maintain testing cadence, validate against ransomware scenarios, and review retention policies.'
    },
    patching: {
      no: 'No formal patching process. Establish risk-based patch management with critical patches applied within 72 hours.',
      partially: 'Ad hoc patching. Formalize patch SLAs by severity, automate where possible, and track compliance metrics.',
      mostly: 'Patching program in place with some gaps. Close visibility gaps for third-party software and validate patch verification.',
      yes: 'Mature patching program. Maintain automation, validate coverage for emerging assets, and review exception processes.'
    },
    segmentation: {
      no: 'Flat network. Implement segmentation between critical systems, user networks, and untrusted zones.',
      partially: 'Basic segmentation exists. Strengthen isolation of critical systems and implement micro-segmentation for sensitive data.',
      mostly: 'Good segmentation. Validate firewall rules between zones, test isolation effectiveness, and address remaining flat segments.',
      yes: 'Well-segmented network. Conduct periodic validation, review rules for drift, and test lateral movement controls.'
    },
    monitoring: {
      no: 'No centralized monitoring. Deploy SIEM with 24/7 SOC coverage, starting with critical log sources.',
      partially: 'Limited monitoring. Expand log source coverage, establish alert triage procedures, and define escalation workflows.',
      mostly: 'Monitoring in place but gaps exist. Tune detection rules, reduce alert fatigue, and ensure coverage of cloud workloads.',
      yes: 'Comprehensive monitoring. Optimize detection logic, reduce mean time to detect, and conduct regular purple team exercises.'
    },
    firewall: {
      'ISP router only': 'No enterprise firewall. Deploy NGFW with IPS, application awareness, and threat intelligence feeds.',
      default: 'Firewall in place. Review and optimize rule sets, enable advanced threat prevention features, and validate policy effectiveness.'
    },
    identity: {
      'None / Local accounts only': 'No centralized identity. Deploy identity platform with SSO, conditional access, and lifecycle management.',
      'On-premises AD only': 'Legacy AD only. Extend to cloud identity with conditional access, MFA integration, and privileged access controls.',
      default: 'Identity platform in place. Enforce conditional access policies, expand SSO coverage, and implement privileged access management.'
    }
  }

  const initNotes = notes[initId]
  if (!initNotes) return null

  // Check maturity controls
  if (['mfa', 'backup', 'patching', 'segmentation', 'monitoring'].includes(initId)) {
    const controlMap = { mfa: 'ma-0', backup: 'ma-23', patching: 'ma-24', segmentation: 'ma-56', monitoring: 'ma-59' }
    const response = ma[controlMap[initId]]
    const value = typeof response === 'object' ? response?.value : response
    return initNotes[value] || initNotes['no']
  }

  // Check technology controls
  if (initId === 'endpoint') {
    const val = ts['ts-4']?.value || ts['ts-4'] || ''
    if (!val || val === 'None') return initNotes['none']
    if (val.includes('Traditional') || val.includes('AV')) return initNotes['Traditional AV only']
    return initNotes['default']
  }
  if (initId === 'firewall') {
    const val = ts['ts-6']?.value || ts['ts-6'] || ''
    if (!val || val.includes('ISP')) return initNotes['ISP router only']
    return initNotes['default']
  }
  if (initId === 'identity') {
    const val = ts['ts-2']?.value || ts['ts-2'] || ''
    if (!val || val.includes('None') || val.includes('Local')) return initNotes['None / Local accounts only']
    if (val.includes('On-premises') || val.includes('AD only')) return initNotes['On-premises AD only']
    return initNotes['default']
  }

  return null
}

// Tier selectors over the canonical Enablement Plan initiative list. Rather
// than hardcoding which initiatives belong in which tier, we derive tier
// membership by ranking the plan's initiatives (which generateEnablementPlan()
// already orders by quadrant then netValue, with priority + tierRelevance
// fields populated from DISCOVER data).
//
// Two parallel sets per tier:
//   • display    → what the advisor sees in the breakdown table
//                   (full enablement plan, governance + technical)
//   • simulation → what's fed to Monte Carlo compareBeforeAfter
//                   (only initiatives matching INITIATIVE_TO_CONTROLS;
//                    falls back to canonical technicals when the plan is
//                    governance-heavy so the chart always renders 3 lines)

// Modelable initiative IDs (must align with INITIATIVE_TO_CONTROLS in
// monteCarloSimulation.js). Used both for filtering and for tier fallbacks.
const MODELABLE_PATTERNS = ['mfa', 'endpoint', 'backup', 'patch', 'vulnerability', 'segmentation', 'siem', 'monitoring', 'firewall', 'identity']

function isModelableInitiative(init) {
  const id = String(init?.id || '').toLowerCase()
  return MODELABLE_PATTERNS.some(p => id.includes(p))
}

// Canonical technical fallbacks per tier — used to populate the Monte Carlo
// simulation when the enablement plan is governance-heavy and produces too
// few modelable initiatives to drive a meaningful chart.
const TIER_TECHNICAL_FALLBACKS = {
  light: [{ id: 'mfa-implementation' }, { id: 'patch-management' }],
  moderate: [{ id: 'mfa-implementation' }, { id: 'endpoint-protection' }, { id: 'patch-management' }, { id: 'backup-validation' }],
  aggressive: [
    { id: 'mfa-implementation' }, { id: 'endpoint-protection' }, { id: 'backup-validation' },
    { id: 'patch-management' }, { id: 'network-segmentation' }, { id: 'siem-soc' },
    { id: 'firewall' }, { id: 'identity-governance' }
  ]
}

const PRIORITY_RANK = { Critical: 0, High: 1, Medium: 2, Low: 3 }

function rankInitiative(init) {
  const p = PRIORITY_RANK[init.priority] ?? 3
  const q = init.quadrant?.quadrant === 'quick-win' ? 0
    : init.quadrant?.quadrant === 'strategic-investment' ? 1
    : init.quadrant?.quadrant === 'efficiency-play' ? 2 : 3
  return [p, q, -(init.netValue || 0)]
}

function compareRanks(a, b) {
  const ra = rankInitiative(a), rb = rankInitiative(b)
  for (let i = 0; i < ra.length; i++) {
    if (ra[i] !== rb[i]) return ra[i] - rb[i]
  }
  return 0
}

const INVESTMENT_TIERS = {
  light: {
    label: 'Light',
    color: '#F59E0B',
    dash: '6 3',
    description: 'Essentials — top quick wins only',
    select: (initiatives) => {
      const eligible = initiatives.filter(i => i.quadrant?.quadrant !== 'defer-accept')
      const quickWins = eligible.filter(i => i.phase === 'quick-wins')
      const sorted = (quickWins.length >= 2 ? quickWins : eligible).slice().sort(compareRanks)
      return sorted.slice(0, 3)
    }
  },
  moderate: {
    label: 'Moderate',
    color: '#3B82F6',
    dash: '4 4',
    description: 'Recommended program — quick wins plus key strategic initiatives',
    select: (initiatives) => {
      const eligible = initiatives.filter(i =>
        i.quadrant?.quadrant !== 'defer-accept' &&
        (i.phase === 'quick-wins' || (i.phase === 'strategic' && (i.priority === 'High' || i.priority === 'Critical')))
      )
      const sorted = eligible.slice().sort(compareRanks)
      // Cap at ~6 so Moderate stays a meaningful step between Light and Aggressive
      return sorted.slice(0, Math.max(6, sorted.length > 8 ? 6 : sorted.length))
    }
  },
  aggressive: {
    label: 'Aggressive',
    color: '#22C55E',
    dash: '2 2',
    description: 'Comprehensive program — all relevant initiatives',
    select: (initiatives) => initiatives
      .filter(i => i.quadrant?.quadrant !== 'defer-accept' && i.priority !== 'Low')
      .slice()
      .sort(compareRanks)
  }
}

function InvestmentPrioritiesTab({ tapsPriorities, getPriorityColor, raw, assessment, client, currentCurve, enablementConfigRow, onUpdateOverrides }) {
  const [selectedTier, setSelectedTier] = useState('moderate')
  const [editingNoteId, setEditingNoteId] = useState(null)
  const responses = assessment?.responses || {}
  const lossScenarios = raw?.lossScenarios?.scenarios || []
  const dynamicCalibration = raw?.dynamicCalibration || {}
  const impactData = responses.impactData || responses.notes?.impactData || {}
  const baseInitiatives = raw?.enablementPlan?.initiatives || []
  const overrides = enablementConfigRow?.initiative_overrides || {}

  // Stamp override status onto each initiative so downstream selectors can
  // reason about it. 'na' is hidden entirely; other statuses stay visible.
  const enablementInitiatives = useMemo(() => {
    return baseInitiatives
      .map(init => {
        const ov = overrides[init.id]
        return {
          ...init,
          overrideStatus: ov?.status || 'active',
          overrideNote: ov?.note || ''
        }
      })
      .filter(init => init.overrideStatus !== 'na')
  }, [baseInitiatives, overrides])

  const setOverride = (initiativeId, patch) => {
    if (!onUpdateOverrides) return
    const next = { ...(overrides || {}) }
    const current = next[initiativeId] || {}
    const merged = { ...current, ...patch }
    if (!merged.status || merged.status === 'active') {
      delete next[initiativeId]
    } else {
      next[initiativeId] = merged
    }
    onUpdateOverrides(next)
  }

  // Group enablement plan initiatives by tier using the rule-based selectors.
  // Each tier inherits whatever initiatives are currently in the canonical
  // plan — when DISCOVER data changes upstream, this re-derives automatically.
  const tierInitiatives = useMemo(() => {
    const grouped = {}
    for (const [tierId, tier] of Object.entries(INVESTMENT_TIERS)) {
      grouped[tierId] = tier.select(enablementInitiatives)
    }
    return grouped
  }, [enablementInitiatives])

  // Build the simulation-side initiative set per tier. We prefer modelable
  // entries from the enablement plan, then top up from canonical technical
  // fallbacks if the plan didn't surface enough technical initiatives for
  // the simulation to produce a non-zero reduction. Initiatives the advisor
  // has marked 'already_in_place' or 'deferred' are excluded from the
  // simulation (already_in_place isn't new investment; deferred is later).
  const simulationInitiatives = useMemo(() => {
    const out = {}
    for (const tierId of Object.keys(INVESTMENT_TIERS)) {
      const fromPlan = (tierInitiatives[tierId] || [])
        .filter(isModelableInitiative)
        .filter(i => i.overrideStatus !== 'already_in_place' && i.overrideStatus !== 'deferred')
      const fallback = TIER_TECHNICAL_FALLBACKS[tierId] || []
      const seen = new Set(fromPlan.map(i => i.id))
      const merged = [...fromPlan]
      for (const f of fallback) {
        const fStatus = overrides[f.id]?.status
        if (fStatus === 'already_in_place' || fStatus === 'deferred' || fStatus === 'na') continue
        if (!seen.has(f.id)) {
          merged.push(f)
          seen.add(f.id)
        }
      }
      out[tierId] = merged
    }
    return out
  }, [tierInitiatives, overrides])

  // Run per-initiative Monte Carlo comparisons for individual risk savings
  const initiativeSavings = useMemo(() => {
    if (lossScenarios.length === 0 || enablementInitiatives.length === 0) return {}
    const savings = {}
    try {
      for (const init of enablementInitiatives) {
        const result = compareBeforeAfter(
          lossScenarios, dynamicCalibration, impactData,
          responses.maturityAssessment || {}, responses.technologyScoping || {},
          [{ id: init.id }], calculateDynamicCalibration,
          responses.businessScoping || {}, 3000
        )
        savings[init.id] = result?.delta?.aleReduction || 0
      }
    } catch { /* incomplete data */ }
    return savings
  }, [lossScenarios, dynamicCalibration, impactData, responses, enablementInitiatives])

  // Tier-level Monte Carlo comparisons (drives the comparison chart). Uses
  // the simulation-side initiative set so the chart always reflects the
  // technical control improvements modelable in the simulation, even when
  // the enablement plan is governance-heavy.
  const tierResults = useMemo(() => {
    if (lossScenarios.length === 0) return null
    const results = {}
    try {
      for (const [tierId] of Object.entries(INVESTMENT_TIERS)) {
        const inits = simulationInitiatives[tierId] || []
        if (inits.length === 0) {
          results[tierId] = null
          continue
        }
        results[tierId] = compareBeforeAfter(
          lossScenarios,
          dynamicCalibration,
          impactData,
          responses.maturityAssessment || {},
          responses.technologyScoping || {},
          inits.map(i => ({ id: i.id })),
          calculateDynamicCalibration,
          responses.businessScoping || {},
          5000
        )
      }
    } catch (err) {
      console.error('[Investment Priorities] Tier comparison error:', err)
      return null
    }
    return results
  }, [lossScenarios, dynamicCalibration, impactData, responses, simulationInitiatives])

  if (tapsPriorities.length === 0 || enablementInitiatives.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <p className="text-sm">Complete the threat context and impact calibration to generate investment priorities.</p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Investment Impact — Monte Carlo Comparison */}
      {tierResults && currentCurve.length > 0 && (
        <InsightCard title="Investment Impact" subtitle="How different levels of security investment shift your risk distribution">
          <InvestmentComparisonChart
            currentCurve={currentCurve}
            tierResults={tierResults}
          />
        </InsightCard>
      )}

      {/* Initiative Detail */}
      {tierResults && (
        <InsightCard title="Initiative Breakdown" subtitle="Cost, controls, and impact for each initiative in the selected investment tier — sourced from the Enablement Plan and updated automatically when DISCOVER data changes">
          {/* Tier filter */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {Object.entries(INVESTMENT_TIERS).map(([tierId, tier]) => {
              const count = tierInitiatives[tierId]?.length || 0
              return (
                <button
                  key={tierId}
                  onClick={() => setSelectedTier(tierId)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    selectedTier === tierId
                      ? 'text-white'
                      : 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  style={selectedTier === tierId ? { backgroundColor: tier.color } : {}}
                >
                  {tier.label} <span className="opacity-70">({count})</span>
                </button>
              )
            })}
          </div>

          {/* Initiative table */}
          {tierInitiatives[selectedTier]?.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic py-4 text-center">
              No initiatives currently meet the {INVESTMENT_TIERS[selectedTier].label.toLowerCase()} tier criteria for this client.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <th className="pb-3 pr-3 w-[24%]">Initiative</th>
                    <th className="pb-3 pr-2 text-center w-[6%]">Priority</th>
                    <th className="pb-3 pr-2 text-center w-[6%]">Effort</th>
                    <th className="pb-3 pr-2 text-center w-[9%]">Est. Cost</th>
                    <th className="pb-3 pr-2 text-center w-[11%]">Cost of Inaction</th>
                    <th className="pb-3 pr-2 text-center w-[9%]">Risk Savings</th>
                    <th className="pb-3 pr-2 text-center w-[14%]">Status</th>
                    <th className="pb-3 w-[21%]">Quadrant / Why</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30">
                  {tierInitiatives[selectedTier].map(init => {
                    const savings = initiativeSavings[init.id] || 0
                    const status = init.overrideStatus || 'active'
                    const isInactive = status === 'already_in_place' || status === 'deferred'
                    const effortColors = { Low: 'text-green-600 dark:text-green-400', Medium: 'text-amber-600 dark:text-amber-400', High: 'text-red-600 dark:text-red-400' }
                    const priorityColors = { Critical: 'text-red-600 dark:text-red-400', High: 'text-orange-600 dark:text-orange-400', Medium: 'text-amber-600 dark:text-amber-400', Low: 'text-gray-500 dark:text-gray-400' }
                    const rowClass = isInactive ? 'opacity-60' : ''
                    return (
                      <tr key={init.id} className={rowClass}>
                        <td className="py-3 pr-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-medium text-gray-900 dark:text-white ${isInactive ? 'line-through decoration-gray-400' : ''}`}>{init.name}</p>
                            {init.categoryId === 'cyber-insurance' && (
                              <span
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                                title="Insurance — coordinate with broker around renewal cycle"
                              >
                                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                                Insurance
                              </span>
                            )}
                          </div>
                          {init.tierRelevance && (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 capitalize">{init.tierRelevance} relevance</p>
                          )}
                          {init.overrideNote && editingNoteId !== init.id && (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 italic">"{init.overrideNote}"</p>
                          )}
                          {editingNoteId === init.id && (
                            <input
                              type="text"
                              autoFocus
                              defaultValue={init.overrideNote || ''}
                              placeholder="Add note (e.g. 'Covered by Arctic Wolf')"
                              onBlur={(e) => {
                                setOverride(init.id, { note: e.target.value.trim() })
                                setEditingNoteId(null)
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }}
                              className="mt-1 w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          )}
                        </td>
                        <td className={`py-3 pr-2 text-xs font-medium text-center whitespace-nowrap ${priorityColors[init.priority] || ''}`}>{init.priority}</td>
                        <td className={`py-3 pr-2 text-xs font-medium text-center whitespace-nowrap ${effortColors[init.effort] || ''}`}>{init.effort}</td>
                        <td className="py-3 pr-2 text-center text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {init.implementationCost?.formatted || '—'}
                        </td>
                        <td className="py-3 pr-2 text-center text-red-600 dark:text-red-400 font-medium whitespace-nowrap">
                          {init.costOfInaction?.annualizedCost > 0 ? fmt(init.costOfInaction.annualizedCost) : '—'}
                          {init.costOfInaction?.confidence && (
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 font-normal">{init.costOfInaction.confidence} confidence</p>
                          )}
                        </td>
                        <td className={`py-3 pr-2 text-center font-medium whitespace-nowrap ${!isInactive && savings > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                          {isInactive ? '—' : savings > 0 ? fmt(savings) : '—'}
                        </td>
                        <td className="py-3 pr-2 text-center">
                          <div className="flex flex-col gap-1 items-stretch">
                            <select
                              value={status}
                              onChange={(e) => setOverride(init.id, { status: e.target.value })}
                              className="text-[11px] px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                              <option value="active">Active</option>
                              <option value="already_in_place">Already in place</option>
                              <option value="deferred">Deferred</option>
                              <option value="na">Not applicable</option>
                            </select>
                            {status !== 'active' && editingNoteId !== init.id && (
                              <button
                                type="button"
                                onClick={() => setEditingNoteId(init.id)}
                                className="text-[10px] text-primary-600 dark:text-primary-400 hover:underline"
                              >
                                {init.overrideNote ? 'Edit note' : 'Add note'}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="py-3 text-xs text-gray-600 dark:text-gray-400">
                          {init.quadrant?.label && (
                            <p className="font-medium text-gray-700 dark:text-gray-300" style={{ color: init.quadrant.color }}>{init.quadrant.label}</p>
                          )}
                          <p className="mt-0.5 line-clamp-2">{init.costOfInaction?.explanation || init.quadrant?.description}</p>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Summary metrics for selected tier */}
          {(() => {
            const inits = tierInitiatives[selectedTier] || []
            const result = tierResults[selectedTier]
            if (!result || inits.length === 0) return null
            const totalCostMin = inits.reduce((sum, i) => sum + (i.implementationCost?.min || 0), 0)
            const totalCostMax = inits.reduce((sum, i) => sum + (i.implementationCost?.max || 0), 0)
            const totalCostOfInaction = inits.reduce((sum, i) => sum + (i.costOfInaction?.annualizedCost || 0), 0)

            return (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/30">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total Investment</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">{fmt(totalCostMin)} - {fmt(totalCostMax)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Cost of Inaction</p>
                    <p className="text-sm font-bold text-red-600 dark:text-red-400">{fmt(totalCostOfInaction)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Risk Reduction</p>
                    <p className="text-sm font-bold text-green-600 dark:text-green-400">{result.delta.aleReductionPercent}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Annual Risk Saved</p>
                    <p className="text-sm font-bold text-green-600 dark:text-green-400">{fmt(result.delta.aleReduction)}</p>
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Link to Enablement Plan */}
          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/30">
            <Link
              to={`/clients/${client?.id}?section=enablement-plan`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
            >
              View Enablement Plan
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </InsightCard>
      )}
    </div>
  )
}

function RiskThreats({ assessmentId, client }) {
  const [enrichmentRow, setEnrichmentRow] = useState(null)
  const [criticalSystems, setCriticalSystems] = useState(null)
  const [controlTestData, setControlTestData] = useState(null)
  const [enablementConfigRow, setEnablementConfigRow] = useState(null)
  const [insuranceReview, setInsuranceReview] = useState(null)
  const { technologies: technologyStack } = useTechnologyStack(client?.id)
  const { data, raw, loading, error } = useInsightsData(assessmentId, {
    enrichmentRow,
    technologyStack,
    criticalSystems,
    controlTestData,
    insuranceReview
  })
  const { getAssessment, saveResponse } = useAssessment()
  const { assessment } = getAssessment(assessmentId) || {}
  const [activeTab, setActiveTab] = useState('landscape')

  // Lazy-load Client Intelligence enrichment so the threat profile can be
  // tuned with subdomain, tech-stack, DNS, and Shodan signals.
  useEffect(() => {
    if (!client?.id) return
    let cancelled = false
    supabase
      .from('client_enrichment')
      .select('*')
      .eq('client_id', client.id)
      .maybeSingle()
      .then(({ data: enrich }) => {
        if (!cancelled && enrich) setEnrichmentRow(enrich)
      })
    return () => { cancelled = true }
  }, [client?.id])

  // Fetch Critical Systems so BIA tier can boost related domain priority
  useEffect(() => {
    if (!client?.id) return
    let cancelled = false
    supabase
      .from('client_critical_systems')
      .select('*')
      .eq('client_id', client.id)
      .then(({ data }) => {
        if (!cancelled) setCriticalSystems(data || [])
      })
    return () => { cancelled = true }
  }, [client?.id])

  // Fetch latest cyber Insurance Policy Review so its AI-extracted
  // aggregate limit, sublimits, exclusions, and high-severity gaps feed
  // Monte Carlo, Adequacy, and Investment Priorities downstream.
  useEffect(() => {
    if (!client?.id) return
    let cancelled = false
    supabase
      .from('client_insurance_policy_reviews')
      .select('id, coverage_limit_aggregate, retention, sublimits, notable_exclusions, findings, reviewed_at')
      .eq('client_id', client.id)
      .order('reviewed_at', { ascending: false })
      .limit(1)
      .then(({ data }) => {
        if (!cancelled) setInsuranceReview(data?.[0] || null)
      })
    return () => { cancelled = true }
  }, [client?.id])

  // Fetch enablement plan config (incl. initiative_overrides — advisor's
  // per-initiative status decisions: already_in_place / deferred / na).
  useEffect(() => {
    if (!client?.id) return
    let cancelled = false
    supabase
      .from('client_enablement_config')
      .select('id, initiative_overrides')
      .eq('client_id', client.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setEnablementConfigRow(data || { id: null, initiative_overrides: {} })
      })
    return () => { cancelled = true }
  }, [client?.id])

  // Fetch latest Control Testing scan + results so failed controls boost
  // the corresponding domain's gap score (and therefore initiative priority).
  useEffect(() => {
    if (!client?.id) return
    let cancelled = false
    ;(async () => {
      const { data: scans } = await supabase
        .from('client_control_test_scans')
        .select('id, scan_type, scanned_at')
        .eq('client_id', client.id)
        .order('scanned_at', { ascending: false })
        .limit(1)
      const latest = scans?.[0]
      if (!latest) {
        if (!cancelled) setControlTestData({ scan: null, results: [] })
        return
      }
      const { data: results } = await supabase
        .from('client_control_test_results')
        .select('test_id, test_name, category, severity, result, framework_tags')
        .eq('scan_id', latest.id)
      if (!cancelled) setControlTestData({ scan: latest, results: results || [] })
    })()
    return () => { cancelled = true }
  }, [client?.id])

  const handleThreatSave = (key, data) => {
    if (!assessmentId) return
    saveResponse(assessmentId, key, null, data)
  }

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
  if (error || !data) return <div className="text-center py-12 text-gray-500 dark:text-gray-400">Unable to load risk analysis.</div>

  const { riskAndThreats } = data
  const hasMonteCarlo = !!riskAndThreats.monteCarlo?.p50
  const hasLossScenarios = riskAndThreats.lossScenarios?.scenarios?.length > 0
  const tp = riskAndThreats.threatProfile
  const tapsPriorities = riskAndThreats.tapsPriorities || []

  // Build executive paragraph
  const topActor = tp.topActors?.[0]?.name
  const topVector = tp.topVectors?.[0]?.name
  const topPriority = tapsPriorities[0]?.domain
  const p90 = riskAndThreats.monteCarlo?.p90
  const incidentProb = riskAndThreats.monteCarlo?.incidentFrequency
    ? Math.round((1 - (riskAndThreats.monteCarlo.incidentFrequency.zeroIncidents || 1)) * 100)
    : null

  const tabs = [
    { id: 'landscape', label: 'Threat Landscape' },
    { id: 'risk', label: 'Risk Profile' },
    { id: 'financial', label: 'Financial Exposure' },
    { id: 'priorities', label: 'Investment Priorities' }
  ]

  const getPriorityColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'critical': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
      case 'high': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
      case 'medium': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
      default: return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
    }
  }

  return (
    <div className="px-1 py-2">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Risk & Threats</h1>
        <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
          Threat landscape, financial exposure, and insurance analysis
        </p>
      </div>

      {/* Executive paragraph */}
      <div className="mb-6 mt-4">
        <p className="text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed">
          {tp.profileName ? (
            <>
              Organizations in <span className="font-medium text-gray-800 dark:text-gray-200">{tp.profileName}</span> face
              elevated risk from {topActor && <span className="font-medium text-gray-800 dark:text-gray-200">{topActor}</span>}
              {topVector && <> primarily through <span className="font-medium text-gray-800 dark:text-gray-200">{topVector.toLowerCase()}</span> attacks</>}.
              {incidentProb && <> Based on current controls, there is a <span className="font-medium text-gray-800 dark:text-gray-200">{incidentProb}% annual probability</span> of experiencing at least one cyber incident</>}
              {p90 ? <> with a 10% chance of losses exceeding <span className="font-medium text-red-600 dark:text-red-400">{fmt(p90)}</span></> : null}.
              {topPriority && <> The greatest exposure is in <span className="font-medium text-gray-800 dark:text-gray-200">{topPriority}</span>.</>}
            </>
          ) : (
            <>Complete the threat context and impact calibration to generate a comprehensive risk analysis.</>
          )}
        </p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200 dark:border-gray-700 mt-4">
        <nav className="flex gap-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="pt-6" />

      {/* Threat Landscape */}
      {activeTab === 'landscape' && (
        <div className="space-y-5">
          {tp.profileName ? (
            <>
              {/* Recommended Threat Profile */}
              <ThreatContextStep
                responses={assessment?.responses || {}}
                onSave={handleThreatSave}
                client={client}
                showOnlyRecommendation
              />

              <InsightCard title="Threat Profile">
                <ThreatProfileSummary
                  topActors={tp.topActors || []}
                  topVectors={tp.topVectors || []}
                  topAssets={tp.topAssets || []}
                  profileName={tp.profileName}
                />
              </InsightCard>

              {/* Named threat actors targeting this vertical (Client Intelligence overlay) */}
              <RelevantThreatActorsCard tuned={raw?.tunedThreatProfile} />

              {/* Technology Stack + Organizational Context */}
              <ThreatContextStep
                responses={assessment?.responses || {}}
                onSave={handleThreatSave}
                client={client}
                hideRecommendation
                hideThreatLandscape
                hideThreatPreview
              />
            </>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <p className="text-sm">Complete the threat context to view your threat profile.</p>
            </div>
          )}
        </div>
      )}

      {/* Risk Profile */}
      {activeTab === 'risk' && (
        <RiskProfileTab raw={raw} data={data} tp={tp} incidentProb={incidentProb} p90={p90} client={client} />
      )}

      {/* Financial Exposure */}
      {activeTab === 'financial' && (
        <div className="space-y-5">
          {/* Monte Carlo */}
          {hasMonteCarlo ? (
            <InsightCard title="Annual Risk Distribution" subtitle="Monte Carlo simulation: 10,000 iterations" accent="#378ADD">
              <ExceedanceCurveChart
                exceedanceCurve={riskAndThreats.monteCarlo.exceedanceCurve}
                percentiles={riskAndThreats.monteCarlo}
                coverageLimit={riskAndThreats.insurance?.coverageLimit}
                recommendedCoverage={riskAndThreats.insurance?.recommendedCoverage}
                recommendedCoverageRationale={riskAndThreats.insurance?.recommendedCoverageRationale}
                executiveSummary={riskAndThreats.monteCarlo.executiveSummary}
              />
            </InsightCard>
          ) : (
            <InsightCard title="Annual Risk Distribution" accent="#378ADD">
              <div className="text-center py-8">
                <svg className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <p className="text-sm text-gray-500 dark:text-gray-400">Complete impact calibration to enable risk simulation</p>
              </div>
            </InsightCard>
          )}

          {/* Link to Financial Resilience for detailed analysis */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/30 rounded-lg">
            <div className="flex-1">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                For detailed loss scenario breakdowns, capital adequacy analysis, and insurance optimization, see Financial Resilience.
              </p>
            </div>
            <a
              href={`/clients/${client?.id}?section=financial-resilience`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
            >
              Financial Resilience
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      )}

      {/* Investment Priorities */}
      {activeTab === 'priorities' && (
        <InvestmentPrioritiesTab
          tapsPriorities={tapsPriorities}
          getPriorityColor={getPriorityColor}
          raw={raw}
          assessment={assessment}
          client={client}
          currentCurve={riskAndThreats.monteCarlo?.exceedanceCurve || []}
          enablementConfigRow={enablementConfigRow}
          onUpdateOverrides={async (newOverrides) => {
            // Optimistic local update
            setEnablementConfigRow(prev => ({ ...(prev || {}), initiative_overrides: newOverrides }))
            const payload = {
              client_id: client.id,
              initiative_overrides: newOverrides
            }
            const existing = enablementConfigRow?.id
            if (existing) {
              await supabase.from('client_enablement_config').update({ initiative_overrides: newOverrides }).eq('id', existing)
            } else {
              const { data: inserted } = await supabase.from('client_enablement_config').insert(payload).select('id').single()
              if (inserted?.id) setEnablementConfigRow(prev => ({ ...(prev || {}), id: inserted.id, initiative_overrides: newOverrides }))
            }
          }}
        />
      )}
    </div>
  )
}

export default RiskThreats
